import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';

// Lazy load non-critical pages for the initial landing render
const DashboardLayout = lazy(() => import('./pages/DashboardLayout'));
const GeneratorPage = lazy(() => import('./pages/dashboard/GeneratorPage'));
const HistoryPage = lazy(() => import('./pages/dashboard/HistoryPage'));
const SavedPage = lazy(() => import('./pages/dashboard/SavedPage'));
const ProfilesPage = lazy(() => import('./pages/dashboard/ProfilesPage'));
const BillingPage = lazy(() => import('./pages/dashboard/BillingPage'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const SEOPage = lazy(() => import('./pages/seo/SEOPage'));
const LongTailSEOPage = lazy(() => import('./pages/seo/LongTailSEOPage'));
const BlogPage = lazy(() => import('./pages/seo/BlogPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const RefundPolicyPage = lazy(() => import('./pages/legal/RefundPolicyPage'));

import { legalUi } from './pages/legal/legalContent';

const SEO_PATHS = [
  'instagram-caption-generator',
  'instagram-bio-generator',
  'hashtag-generator',
  'instagram-caption-generator-for-creators',
  'instagram-caption-generator-for-small-business',
  'instagram-caption-generator-for-beauty'
];

const LONGTAIL_PATHS = [
  'captions/travel',
  'captions/fitness',
  'captions/beauty',
  'bio/creator',
  'bio/small-business',
  'hashtags/reels'
];

const BLOG_PATHS = [
  'blog/how-to-write-instagram-captions',
  'blog/best-instagram-bio-ideas',
  'blog/best-hashtags-for-instagram'
];

function ScrollToHash() {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = hash.replace('#', '');
    let attempts = 0;
    const maxAttempts = 30;
    let timer;

    const scrollWhenReady = () => {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        return;
      }

      attempts += 1;

      if (attempts < maxAttempts) {
        timer = window.setTimeout(scrollWhenReady, 150);
      }
    };

    timer = window.setTimeout(scrollWhenReady, 100);

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [pathname, hash]);

  return null;
}

function LandingShell() {
  const { pathname } = useLocation();
  const pathParts = pathname.split('/').filter(Boolean);
  const locale = ['pt', 'es', 'fr', 'de'].includes(pathParts[0]) ? pathParts[0] : 'en';
  const legalBase = locale === 'en' ? '' : `/${locale}`;
  const legalText = legalUi[locale];

  return (
    <>
      <SignedOut>
        <Navbar />
        <LandingPage />
        <footer
          className="footer"
          style={{
            padding: '60px 5vw',
            borderTop: '0.5px solid var(--border)',
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: '0.8rem',
          }}
        >
          <div style={{ marginBottom: 10 }}>&copy; 2026 CaptionScript · Better content, less effort.</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '18px', flexWrap: 'wrap' }}>
            <a href={`${legalBase}/privacy` || '/privacy'} style={{ color: 'var(--muted)', textDecoration: 'none' }}>{legalText.footerPrivacy}</a>
            <a href={`${legalBase}/terms` || '/terms'} style={{ color: 'var(--muted)', textDecoration: 'none' }}>{legalText.footerTerms}</a>
            <a href={`${legalBase}/refund-policy` || '/refund-policy'} style={{ color: 'var(--muted)', textDecoration: 'none' }}>{legalText.footerRefund}</a>
          </div>
        </footer>
      </SignedOut>

      <SignedIn>
        <Navigate to="/dashboard" replace />
      </SignedIn>
    </>
  );
}

function App() {
  return (
    <div className="app-container">
      <ScrollToHash />

      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<LandingShell />} />

          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />

          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/en/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/en/terms" element={<TermsPage />} />
          <Route path="/en/refund-policy" element={<RefundPolicyPage />} />

          <Route
            path="/dashboard"
            element={
              <>
                <SignedIn>
                  <DashboardLayout />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          >
            <Route index element={<Navigate to="generator" replace />} />
            <Route path="generator" element={<GeneratorPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="saved" element={<SavedPage />} />
            <Route path="profiles" element={<ProfilesPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {SEO_PATHS.map(tool => (
            <Route key={tool} path={`/${tool}`} element={<SEOPage />} />
          ))}

          {LONGTAIL_PATHS.map(tool => (
            <Route key={tool} path={`/${tool}`} element={<LongTailSEOPage />} />
          ))}

          {BLOG_PATHS.map(tool => (
            <Route key={tool} path={`/${tool}`} element={<BlogPage />} />
          ))}

          {['pt', 'es', 'fr', 'de'].map(lang => (
            <React.Fragment key={lang}>
              <Route path={`/${lang}`} element={<LandingShell />} />
              <Route path={`/${lang}/privacy`} element={<PrivacyPolicyPage />} />
              <Route path={`/${lang}/terms`} element={<TermsPage />} />
              <Route path={`/${lang}/refund-policy`} element={<RefundPolicyPage />} />

              {SEO_PATHS.map(tool => (
                <Route
                  key={`${lang}-${tool}`}
                  path={`/${lang}/${tool}`}
                  element={<SEOPage />}
                />
              ))}

              {LONGTAIL_PATHS.map(tool => (
                <Route
                  key={`${lang}-${tool}`}
                  path={`/${lang}/${tool}`}
                  element={<LongTailSEOPage />}
                />
              ))}

              {BLOG_PATHS.map(tool => (
                <Route
                  key={`${lang}-${tool}`}
                  path={`/${lang}/${tool}`}
                  element={<BlogPage />}
                />
              ))}
            </React.Fragment>
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;